import React, { useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Store, Plus, Trash2, Loader2, CheckCircle2, XCircle, Globe } from "lucide-react";
import { MenuSnippetButton } from "@/components/PromoMessage";

const TIMEZONES = [
  ["America/St_Johns", "Newfoundland (St. John's)"],
  ["America/Halifax", "Atlantic (Halifax, Sydney NS)"],
  ["America/Moncton", "Atlantic (Moncton)"],
  ["America/Toronto", "Eastern (Toronto)"],
  ["America/New_York", "Eastern (New York)"],
  ["America/Winnipeg", "Central (Winnipeg)"],
  ["America/Chicago", "Central (Chicago)"],
  ["America/Regina", "Saskatchewan (Regina)"],
  ["America/Edmonton", "Mountain (Edmonton)"],
  ["America/Vancouver", "Pacific (Vancouver)"],
  ["UTC", "UTC"],
];

const TimezoneSelect = ({ value, onValueChange, testId }) => (
  <Select value={value} onValueChange={onValueChange}>
    <SelectTrigger className="h-8 text-xs" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      {(TIMEZONES.some(([v]) => v === value) ? TIMEZONES : [[value, value], ...TIMEZONES]).map(([v, l]) => (
        <SelectItem key={v} value={v} data-testid={`${testId}-${v}`}>{l}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export default function SitesManager({ sites, activeSite, onChange }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", url: "", consumer_key: "", consumer_secret: "", timezone: "UTC", addon_plugin: "pewc" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const create = async () => {
    if (!form.name || !form.url || !form.consumer_key || !form.consumer_secret)
      return toast.error("Fill in all fields");
    setSaving(true);
    try {
      await api.post("/sites", { ...form, demo: false });
      toast.success("Site connected");
      setOpen(false);
      setForm({ name: "", url: "", consumer_key: "", consumer_secret: "", timezone: "UTC", addon_plugin: "pewc" });
      onChange();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const testConn = async (id) => {
    setTesting(id);
    try {
      const r = await api.post(`/sites/${id}/test`);
      r.data.connected ? toast.success("Connection OK") : toast.error(r.data.error || "Connection failed");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setTesting(null);
    }
  };

  const updateTz = async (s, timezone) => {
    try {
      await api.put(`/sites/${s.id}`, { timezone });
      toast.success(`${s.name} timezone set to ${timezone}`);
      onChange();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/sites/${deleteTarget.id}`);
      toast.success("Site removed");
      setDeleteTarget(null);
      onChange();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-xl font-semibold">WooCommerce Site Connections</h3>
          <p className="text-sm text-muted-foreground">Connect a store via REST API Consumer Key &amp; Secret.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="add-site-button">
          <Plus className="w-4 h-4" /> Add site
        </Button>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {sites.map((s) => (
          <div
            key={s.id}
            className={`rounded-lg border bg-card p-4 flex items-center justify-between gap-3 ${
              activeSite?.id === s.id ? "border-primary/50" : "border-border"
            }`}
            data-testid={`site-card-${s.id}`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Store className="w-4 h-4 text-primary shrink-0" />
                <span className="font-heading font-semibold truncate">{s.name}</span>
                {s.demo && <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/30 text-[10px]">DEMO</Badge>}
              </div>
              <p className="text-xs text-muted-foreground font-mono-num truncate mt-1">{s.url || "In-app demo store"}</p>
              <div className="mt-2 w-60">
                <TimezoneSelect value={s.timezone} onValueChange={(v) => updateTz(s, v)} testId={`site-tz-select-${s.id}`} />
              </div>
            </div>
            <div className="flex flex-col gap-1.5 shrink-0">
              {!s.demo && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => testConn(s.id)}
                  disabled={testing === s.id}
                  data-testid={`test-site-${s.id}`}
                >
                  {testing === s.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Globe className="w-3.5 h-3.5" />
                  )}
                  Test
                </Button>
              )}
              {!s.demo && <MenuSnippetButton site={s} />}
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => setDeleteTarget(s)}
                data-testid={`delete-site-${s.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Add dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-site-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Connect a WooCommerce site</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Site name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Downtown Pizzeria"
                data-testid="site-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Store URL</Label>
              <Input
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://mypizzastore.com"
                data-testid="site-url-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Consumer Key</Label>
              <Input
                value={form.consumer_key}
                onChange={(e) => setForm({ ...form, consumer_key: e.target.value })}
                placeholder="ck_..."
                className="font-mono-num"
                data-testid="site-ck-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Consumer Secret</Label>
              <Input
                type="password"
                value={form.consumer_secret}
                onChange={(e) => setForm({ ...form, consumer_secret: e.target.value })}
                placeholder="cs_..."
                className="font-mono-num"
                data-testid="site-cs-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Timezone</Label>
              <TimezoneSelect value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })} testId="site-tz-input" />
            </div>
            <div className="space-y-2">
              <Label>Add-On plugin</Label>
              <Select value={form.addon_plugin} onValueChange={(v) => setForm({ ...form, addon_plugin: v })}>
                <SelectTrigger data-testid="site-addon-plugin"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pewc">Plugin Republic — Product Add-Ons Ultimate</SelectItem>
                  <SelectItem value="official">Official WooCommerce Product Add-Ons</SelectItem>
                  <SelectItem value="none">No add-ons</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">
              Create a Read/Write REST API key in WooCommerce → Settings → Advanced → REST API. The connection is
              verified before saving.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={create} disabled={saving} data-testid="save-site-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-site-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the connection and all its promotions from Kenny's Pizza. Your WooCommerce data is not
              deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="confirm-delete-site">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
